// src/components/UnifiedSurvey.jsx - DVA Design System Refactor
// ============================================================
// DESIGN REFACTOR: Full DVA design system alignment
// - Left rail: Clean white background, orange completed states
// - Progress: Prominent percentage, orange bar
// - Questions: Rounded cards, orange accents, polished hover
// - Navigation: Proper button hierarchy, DVA colors
// CLIENT ID: Required prop from parent - must be STRING type
// ============================================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AlertCircle, ArrowLeft, ArrowRight, Check } from 'lucide-react';
import {
  clearProgressCache,
  completeSurvey,
  countAnsweredSurveyResponses,
  isSurveyAnswerAnswered,
  saveSurveyAnswer,
} from '@/services/surveyService';

const getQuestionSectionKey = (question) =>
  question?.section_id ?? question?.sectionId ?? question?.section ?? null;

const questionsHaveSectionMetadata = (questionList) =>
  questionList.some((q) => {
    const key = getQuestionSectionKey(q);
    return key != null && key !== '';
  });

const formatSectionTitle = (id) =>
  String(id)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const deriveSectionsFromQuestions = (questionList, apiSections = []) => {
  const apiById = new Map(apiSections.map((section) => [section.id, section]));
  const ordered = [];
  const seen = new Set();

  questionList.forEach((question) => {
    const id = getQuestionSectionKey(question);
    if (!id || seen.has(id)) return;
    seen.add(id);
    const fromApi = apiById.get(id);
    ordered.push(fromApi ?? { id, title: formatSectionTitle(id) });
  });

  return ordered;
};

const resolveSurveySections = (questionList, apiSections, fallbackSections) => {
  if (!questionsHaveSectionMetadata(questionList)) {
    return apiSections?.length ? apiSections : fallbackSections;
  }

  const derived = deriveSectionsFromQuestions(questionList, apiSections);
  return derived.length > 0 ? derived : (apiSections?.length ? apiSections : fallbackSections);
};

const questionBelongsToSection = (question, section) => {
  const questionSectionKey = getQuestionSectionKey(question);
  if (!questionSectionKey || !section) return false;

  if (questionSectionKey === section.id) return true;
  if (section.title && questionSectionKey === section.title) return true;
  return false;
};

const getQuestionsForSection = (questionList, sectionList, sectionIndex) => {
  if (!sectionList[sectionIndex]) return questionList;

  const section = sectionList[sectionIndex];

  if (questionsHaveSectionMetadata(questionList)) {
    return questionList.filter((question) => questionBelongsToSection(question, section));
  }

  const bucketSize = Math.ceil(questionList.length / sectionList.length);
  return questionList.filter((_, idx) => Math.floor(idx / bucketSize) === sectionIndex);
};

// DVA Brand Colors
const DVA_COLORS = {
  navy: '#18365E',
  orange: '#FF6E4C',
  orangeLight: '#FFF4F1',
  orangeMuted: '#FFEBE6',
  slate: '#717182',
  slateLight: '#F5F7FA',
  border: '#E5E7EB',
};

const UnifiedSurvey = ({
  isOpen,
  onClose,
  userProfile,
  clientId,
  userId,
  surveyType = 'strategy',
  onComplete,
  onSaveAndExit,
  onSectionComplete,
}) => {
  const [questions, setQuestions] = useState([]);
  const [sections, setSections] = useState([]);
  const [responses, setResponses] = useState({});
  const [currentSection, setCurrentSection] = useState(0);
  const [previousSection, setPreviousSection] = useState(null);
  const [responsesLoaded, setResponsesLoaded] = useState(false);
  const hasAutoJumpedRef = useRef(false);
  const [progress, setProgress] = useState({
    answered: 0,
    total: 0,
    percentage: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [error, setError] = useState(null);
  const [completeError, setCompleteError] = useState(null);
  const [saveStatus, setSaveStatus] = useState('');
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const saveIndicatorRef = useRef(null);

  const validClientId = clientId ? String(clientId) : null;
  const validUserId = userId ? String(userId) : null;

  const surveyConfig = {
    strategy: { 
      totalQuestions: 9, 
      title: 'Strategic Priorities',
      sections: [
        { id: 'strategic_vision', title: 'Strategic Vision', order: 1 },
        { id: 'data_innovation', title: 'Data & Innovation', order: 2 },
        { id: 'financial_objectives', title: 'Financial Objectives', order: 3 },
        { id: 'optional_context', title: 'Optional Context', order: 4 }
      ]
    },
    capabilities: { 
      totalQuestions: 14, 
      title: 'Data & Analytics Capabilities',
      sections: [
        { id: 'infrastructure', title: 'Infrastructure', order: 1 },
        { id: 'analytics', title: 'Analytics', order: 2 },
        { id: 'governance', title: 'Governance', order: 3 }
      ]
    },
    readiness: { 
      totalQuestions: 14, 
      title: 'Data Readiness & Governance',
      sections: [
        { id: 'data_quality', title: 'Data Quality', order: 1 },
        { id: 'compliance', title: 'Compliance', order: 2 },
        { id: 'organizational', title: 'Organizational', order: 3 }
      ]
    }
  };

  const config = surveyConfig[surveyType] || surveyConfig.strategy;

  // No client ID error state
  if (!validClientId) {
    return (
      <div className="flex items-center justify-center h-full bg-[#F5F7FA]">
        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-8 max-w-md text-center shadow-sm">
          <div 
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: DVA_COLORS.orangeLight }}
          >
            <AlertCircle className="w-8 h-8" style={{ color: DVA_COLORS.orange }} />
          </div>
          <p className="text-[#18365E] font-semibold text-lg mb-2">Unable to Load Module</p>
          <p className="text-[#717182] text-sm">
            No organization selected. Please ensure you are logged in and associated with an organization.
          </p>
        </div>
      </div>
    );
  }

  // Load survey questions
  const loadSurveyQuestions = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(
        `${import.meta.env.VITE_BACKEND_URL || 'http://localhost:8080'}/api/survey/questions/${surveyType}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );

      if (!response.ok) throw new Error(`Failed to load questions: ${response.status}`);

      const data = await response.json();
      let parsedQuestions = [];
      let parsedSections = data.sections || [];

      if (data.questions && Array.isArray(data.questions)) {
        parsedQuestions = data.questions;
      } else if (data.bySection) {
        if (!parsedSections.length) {
          parsedSections = Object.entries(data.bySection).map(([sectionKey, section]) => ({
            id: section.id || sectionKey,
            title: section.title || formatSectionTitle(section.id || sectionKey),
            order: section.order,
          }));
        }
        Object.values(data.bySection).forEach((section) => {
          if (section.questions) {
            parsedQuestions.push(...section.questions);
          }
        });
      } else if (Array.isArray(data)) {
        parsedQuestions = data;
      }

      parsedSections = resolveSurveySections(parsedQuestions, parsedSections, config.sections || []);
      setQuestions(parsedQuestions);
      setSections(parsedSections);
      
      setProgress({
        answered: 0,
        total: parsedQuestions.length || config.totalQuestions,
        percentage: 0
      });

    } catch (error) {
      console.error('Error loading survey questions:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Load saved responses
  const loadSavedResponses = async () => {
    if (!validClientId || !validUserId) {
      setResponsesLoaded(true);
      return;
    }

    try {
      const response = await fetch(
        `${import.meta.env.VITE_BACKEND_URL || 'http://localhost:8080'}/api/survey/${surveyType}/responses?client_id=${validClientId}&user_id=${validUserId}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );

      if (!response.ok) {
        setResponsesLoaded(true);
        return;
      }

      const data = await response.json();
      let parsedResponses = {};
      
      if (Array.isArray(data)) {
        data.forEach(item => {
          if (item.question_id && isSurveyAnswerAnswered(item.response)) {
            parsedResponses[item.question_id] = item.response;
          }
        });
      } else if (data.responses) {
        if (Array.isArray(data.responses)) {
        data.responses.forEach(item => {
          if (item.question_id && isSurveyAnswerAnswered(item.response)) {
            parsedResponses[item.question_id] = item.response;
          }
        });
        } else if (typeof data.responses === 'object') {
          Object.entries(data.responses).forEach(([questionId, response]) => {
            if (isSurveyAnswerAnswered(response)) {
              parsedResponses[questionId] = response;
            }
          });
        }
      }
      
      if (Object.keys(parsedResponses).length > 0) {
        setResponses(parsedResponses);
        const totalQuestions = questions.length || config.totalQuestions;
        const answeredCount = countAnsweredSurveyResponses(parsedResponses);
        const percentage = totalQuestions > 0
          ? Math.min(100, Math.round((answeredCount / totalQuestions) * 100))
          : 0;

        setProgress({
          answered: answeredCount,
          total: totalQuestions,
          percentage: percentage
        });
      }
    } catch (error) {
      console.error('Error loading saved responses:', error);
    } finally {
      setResponsesLoaded(true);
    }
  };

  useEffect(() => {
    if (isOpen && validClientId) loadSurveyQuestions();
  }, [isOpen, surveyType, validClientId]);

  useEffect(() => {
    if (isOpen && questions.length > 0 && validClientId) loadSavedResponses();
  }, [isOpen, questions.length, validClientId]);

  // Reset auto-jump state when modal closes or survey type changes,
  // so the next open re-evaluates which section to land on.
  useEffect(() => {
    if (!isOpen) {
      hasAutoJumpedRef.current = false;
      setResponsesLoaded(false);
      setResponses({});
      setCurrentSection(0);
      setPreviousSection(null);
    }
  }, [isOpen]);

  useEffect(() => {
    hasAutoJumpedRef.current = false;
    setResponsesLoaded(false);
  }, [surveyType]);

  // Auto-jump to the first incomplete section once questions, sections,
  // and saved responses have loaded. Only runs once per modal open.
  useEffect(() => {
    if (!isOpen) return;
    if (hasAutoJumpedRef.current) return;
    if (!responsesLoaded) return;
    if (questions.length === 0 || sections.length === 0) return;

    const firstIncompleteIndex = sections.findIndex((_, idx) => {
      const sectionQuestions = getQuestionsForSection(questions, sections, idx);
      if (sectionQuestions.length === 0) return false;
      const answered = sectionQuestions.filter(q => {
        const qId = q.question_id || q.id;
        const value = responses[qId];
        return value !== null && value !== undefined && value !== '';
      }).length;
      return answered < sectionQuestions.length;
    });

    const targetSection = firstIncompleteIndex === -1 ? 0 : firstIncompleteIndex;
    setCurrentSection(targetSection);
    setPreviousSection(targetSection);
    hasAutoJumpedRef.current = true;
  }, [isOpen, responsesLoaded, questions, sections, responses]);

  // Track section changes for completion messaging
  useEffect(() => {
    if (previousSection !== null && previousSection !== currentSection) {
      const prevSectionData = sections[previousSection];
      if (prevSectionData && onSectionComplete) {
        const sectionProgress = getSectionProgress(previousSection);
        if (sectionProgress.answered > 0) {
          onSectionComplete(prevSectionData.title);
        }
      }
    }
    setPreviousSection(currentSection);
  }, [currentSection, sections]);

  // Handle response changes with auto-save
  const handleResponseChange = useCallback(async (questionId, value) => {
    if (!validClientId) {
      console.error('[UnifiedSurvey] Cannot save - clientId is missing');
      setSaveStatus('error');
      return;
    }

    const newResponses = { ...responses };
    if (isSurveyAnswerAnswered(value)) {
      newResponses[questionId] = value;
    } else {
      delete newResponses[questionId];
    }
    setResponses(newResponses);

    const answeredCount = countAnsweredSurveyResponses(newResponses);
    
    const percentage = Math.min(100, Math.round((answeredCount / progress.total) * 100));
    
    setProgress(prev => ({
      ...prev,
      answered: answeredCount,
      percentage: percentage
    }));

    try {
      setIsSaving(true);
      setSaveStatus('saving');

      const normalizedResponse = isSurveyAnswerAnswered(value) ? value : null;
      await saveSurveyAnswer(surveyType, {
        client_id: validClientId,
        user_id: validUserId,
        question_id: questionId,
        response: normalizedResponse,
      });
      clearProgressCache(validClientId);

      setSaveStatus('saved');
      setLastSaveTime(new Date());
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  }, [surveyType, validClientId, validUserId, responses, progress.total]);

  const handleComplete = async () => {
    if (progress.answered < progress.total) {
      if (!window.confirm(`You have answered ${progress.answered} out of ${progress.total} inputs. Complete anyway?`)) {
        return;
      }
    }
    if (isCompleting) return;

    setIsCompleting(true);
    setCompleteError(null);
    try {
      await completeSurvey(surveyType, {
        client_id: validClientId,
        user_id: validUserId || 'system',
      });
      if (onComplete) {
        await onComplete({ surveyType, clientId: validClientId, userId: validUserId, responses, progress });
      }
    } catch (completeErr) {
      console.error('Failed to complete survey / generate summary:', completeErr);
      setCompleteError(
        completeErr.response?.data?.error ||
        completeErr.message ||
        'Survey answers were saved, but summary generation failed.'
      );
    } finally {
      setIsCompleting(false);
    }
  };

  const handleSaveAndReturn = () => {
    if (onSaveAndExit) {
      onSaveAndExit({ surveyType, clientId: validClientId, userId: validUserId, responses, progress });
    }
  };

  const getCurrentSectionQuestions = () =>
    getQuestionsForSection(questions, sections, currentSection);

  const getSectionProgress = (sectionIndex) => {
    const sectionQuestions = getQuestionsForSection(questions, sections, sectionIndex);
    if (sectionQuestions.length === 0) return { answered: 0, total: 0 };
    
    const answeredCount = sectionQuestions.filter(q => {
      const qId = q.question_id || q.id;
      return isSurveyAnswerAnswered(responses[qId]);
    }).length;
    
    return { answered: answeredCount, total: sectionQuestions.length };
  };

  // Render Save Indicator in header
  useEffect(() => {
    const container = document.getElementById('save-indicator-container');
    if (container && saveIndicatorRef.current) {
      const indicator = saveIndicatorRef.current;
      if (container.firstChild !== indicator) {
        container.insertBefore(indicator, container.firstChild);
      }
    }
  }, [saveStatus]);

  // ============================================================
  // RENDER QUESTION - DVA Styled Inputs
  // ============================================================
  const renderQuestion = (question) => {
    const questionId = question.question_id || question.id;
    const currentValue = responses[questionId] || '';
    const responseType = question.response_type || question.type || 'short_text';

    // Single Select (Radio)
    if (responseType === 'single_select' && question.options) {
      return (
        <div className="space-y-3">
          {question.options.map((option, idx) => {
            const optionValue = typeof option === 'string' ? option : (option.value || option.label);
            const optionLabel = typeof option === 'string' ? option : (option.label || option.value);
            const isSelected = currentValue === optionValue;
            
            return (
              <label
                key={idx}
                className="flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-200"
                style={{
                  borderColor: isSelected ? DVA_COLORS.orange : '#E5E7EB',
                  backgroundColor: isSelected ? DVA_COLORS.orangeLight : 'white',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.borderColor = '#CBD5E1';
                    e.currentTarget.style.backgroundColor = '#F8FAFC';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.borderColor = '#E5E7EB';
                    e.currentTarget.style.backgroundColor = 'white';
                  }
                }}
              >
                <div 
                  className="w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all"
                  style={{
                    borderColor: isSelected ? DVA_COLORS.orange : '#CBD5E1',
                    backgroundColor: isSelected ? DVA_COLORS.orange : 'white',
                  }}
                >
                  {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                </div>
                <input
                  type="radio"
                  name={`question-${questionId}`}
                  value={optionValue}
                  checked={isSelected}
                  onChange={() => handleResponseChange(questionId, optionValue)}
                  className="sr-only"
                />
                <span 
                  className="ml-3 text-sm font-medium"
                  style={{ color: isSelected ? DVA_COLORS.navy : '#374151' }}
                >
                  {optionLabel}
                </span>
              </label>
            );
          })}
        </div>
      );
    }

    // Multi Select (Checkbox)
    if (responseType === 'multi_select' && question.options) {
      const selectedValues = Array.isArray(currentValue) ? currentValue : [];
      
      return (
        <div className="space-y-3">
          {question.options.map((option, idx) => {
            const optionValue = typeof option === 'string' ? option : (option.value || option.label);
            const optionLabel = typeof option === 'string' ? option : (option.label || option.value);
            const isSelected = selectedValues.includes(optionValue);
            
            return (
              <label
                key={idx}
                className="flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-200"
                style={{
                  borderColor: isSelected ? DVA_COLORS.orange : '#E5E7EB',
                  backgroundColor: isSelected ? DVA_COLORS.orangeLight : 'white',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.borderColor = '#CBD5E1';
                    e.currentTarget.style.backgroundColor = '#F8FAFC';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.borderColor = '#E5E7EB';
                    e.currentTarget.style.backgroundColor = 'white';
                  }
                }}
              >
                <div 
                  className="w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-all"
                  style={{
                    borderColor: isSelected ? DVA_COLORS.orange : '#CBD5E1',
                    backgroundColor: isSelected ? DVA_COLORS.orange : 'white',
                  }}
                >
                  {isSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                </div>
                <input
                  type="checkbox"
                  value={optionValue}
                  checked={isSelected}
                  onChange={(e) => {
                    let newValues = [...selectedValues];
                    if (e.target.checked) {
                      newValues.push(optionValue);
                    } else {
                      newValues = newValues.filter(v => v !== optionValue);
                    }
                    handleResponseChange(questionId, newValues);
                  }}
                  className="sr-only"
                />
                <span 
                  className="ml-3 text-sm font-medium"
                  style={{ color: isSelected ? DVA_COLORS.navy : '#374151' }}
                >
                  {optionLabel}
                </span>
              </label>
            );
          })}
        </div>
      );
    }

    // Long Text
    if (responseType === 'long_text') {
      return (
        <textarea
          value={currentValue}
          onChange={(e) => handleResponseChange(questionId, e.target.value)}
          placeholder="Enter your response..."
          className="w-full p-4 border-2 rounded-xl min-h-[140px] text-sm resize-none transition-all duration-200 focus:outline-none"
          style={{
            borderColor: currentValue ? DVA_COLORS.orange : '#E5E7EB',
            color: DVA_COLORS.navy,
          }}
          onFocus={(e) => e.target.style.borderColor = DVA_COLORS.orange}
          onBlur={(e) => {
            if (!currentValue) e.target.style.borderColor = '#E5E7EB';
          }}
        />
      );
    }

    // Short Text (default)
    return (
      <input
        type="text"
        value={currentValue}
        onChange={(e) => handleResponseChange(questionId, e.target.value)}
        placeholder="Enter your response..."
        className="w-full p-4 border-2 rounded-xl text-sm transition-all duration-200 focus:outline-none"
        style={{
          borderColor: currentValue ? DVA_COLORS.orange : '#E5E7EB',
          color: DVA_COLORS.navy,
        }}
        onFocus={(e) => e.target.style.borderColor = DVA_COLORS.orange}
        onBlur={(e) => {
          if (!currentValue) e.target.style.borderColor = '#E5E7EB';
        }}
      />
    );
  };

  // ============================================================
  // LOADING STATE
  // ============================================================
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full" style={{ backgroundColor: DVA_COLORS.slateLight }}>
        <div className="text-center">
          <div 
            className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-t-transparent"
            style={{ borderColor: `${DVA_COLORS.orange} transparent ${DVA_COLORS.orange} ${DVA_COLORS.orange}` }}
          />
          <p className="mt-4 text-sm" style={{ color: DVA_COLORS.slate }}>Loading module...</p>
        </div>
      </div>
    );
  }

  // ============================================================
  // ERROR STATE
  // ============================================================
  if (error) {
    return (
      <div className="flex items-center justify-center h-full" style={{ backgroundColor: DVA_COLORS.slateLight }}>
        <div className="bg-white border border-red-200 rounded-2xl p-8 max-w-md text-center shadow-sm">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <p className="text-red-700 font-semibold text-lg mb-2">Error Loading Module</p>
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const currentSectionQuestions = getCurrentSectionQuestions();
  const currentSectionData = sections[currentSection] || { title: 'Questions' };
  const isModuleComplete = progress.percentage === 100;

  return (
    <>
      {/* Save Indicator Portal */}
      <div ref={saveIndicatorRef} className="flex items-center gap-2 mr-4">
        {saveStatus === 'saving' && (
          <span className="text-sm animate-pulse" style={{ color: DVA_COLORS.slate }}>Saving...</span>
        )}
        {saveStatus === 'saved' && (
          <span className="text-sm flex items-center gap-1.5" style={{ color: DVA_COLORS.orange }}>
            <Check className="w-4 h-4" />
            Saved
            {lastSaveTime && (
              <span className="text-xs ml-1" style={{ color: DVA_COLORS.slate }}>
                {lastSaveTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </span>
        )}
        {saveStatus === 'error' && (
          <span className="text-sm text-red-500">Failed to save</span>
        )}
      </div>

      {completeError && (
        <div className="mx-8 mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700 font-medium">Could not generate survey summary</p>
          <p className="text-red-600 text-sm mt-1">{completeError}</p>
        </div>
      )}

      <div className="flex h-full">
        {/* ============================================================
            LEFT RAIL - Clean DVA Design
            ============================================================ */}
        <div 
          className="w-64 shrink-0 flex flex-col border-r"
          style={{ backgroundColor: 'white', borderColor: DVA_COLORS.border }}
        >
          {/* Progress Card */}
          <div className="p-5 border-b" style={{ borderColor: DVA_COLORS.border }}>
            <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: DVA_COLORS.slate }}>
              Progress
            </p>
            <div 
              className="rounded-xl p-4"
              style={{ backgroundColor: DVA_COLORS.slateLight }}
            >
              <div className="flex items-baseline justify-between mb-2">
                <span 
                  className="text-3xl font-bold"
                  style={{ color: DVA_COLORS.navy }}
                >
                  {progress.percentage}%
                </span>
                <span className="text-sm font-medium" style={{ color: DVA_COLORS.slate }}>
                  {progress.answered}/{progress.total}
                </span>
              </div>
              <div 
                className="h-2 rounded-full overflow-hidden"
                style={{ backgroundColor: '#E5E7EB' }}
              >
                <div 
                  className="h-full rounded-full transition-all duration-500"
                  style={{ 
                    width: `${progress.percentage}%`,
                    backgroundColor: DVA_COLORS.orange
                  }}
                />
              </div>
            </div>
          </div>

          {/* Section Navigation */}
          <div className="flex-1 overflow-y-auto p-5">
            <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: DVA_COLORS.slate }}>
              Module Sections
            </p>
            <div className="space-y-2">
              {sections.map((section, idx) => {
                const sectionProgress = getSectionProgress(idx);
                const isComplete = sectionProgress.answered === sectionProgress.total && sectionProgress.total > 0;
                const isCurrent = currentSection === idx;
                
                return (
                  <button
                    key={idx}
                    onClick={() => setCurrentSection(idx)}
                    className="w-full text-left p-3 rounded-xl transition-all duration-200 flex items-center gap-3"
                    style={{
                      backgroundColor: isCurrent ? DVA_COLORS.slateLight : 'transparent',
                      border: isCurrent ? `2px solid ${DVA_COLORS.navy}` : '2px solid transparent',
                    }}
                    onMouseEnter={(e) => {
                      if (!isCurrent) {
                        e.currentTarget.style.backgroundColor = DVA_COLORS.slateLight;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isCurrent) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    {/* Status Icon */}
                    <div 
                      className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0"
                      style={{
                        backgroundColor: isComplete 
                          ? DVA_COLORS.orangeLight
                          : isCurrent 
                            ? DVA_COLORS.navy 
                            : '#F1F5F9',
                        color: isComplete 
                          ? DVA_COLORS.orange
                          : isCurrent 
                            ? 'white' 
                            : DVA_COLORS.slate,
                      }}
                    >
                      {isComplete ? <Check className="w-4 h-4" strokeWidth={3} /> : idx + 1}
                    </div>
                    
                    {/* Section Info */}
                    <div className="flex-1 min-w-0">
                      <p 
                        className="text-sm font-medium truncate"
                        style={{ color: isCurrent ? DVA_COLORS.navy : '#374151' }}
                      >
                        {section.title}
                      </p>
                      {sectionProgress.total > 0 && (
                        <p className="text-xs" style={{ color: DVA_COLORS.slate }}>
                          {sectionProgress.answered}/{sectionProgress.total} complete
                        </p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* ============================================================
            MAIN CONTENT AREA
            ============================================================ */}
        <div className="flex-1 flex flex-col" style={{ backgroundColor: DVA_COLORS.slateLight }}>
          {/* Questions Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-3xl mx-auto p-8">
              {/* Section Header */}
              <div className="mb-8">
                <h2 
                  className="text-2xl font-bold mb-1"
                  style={{ color: DVA_COLORS.navy }}
                >
                  {currentSectionData.title}
                </h2>
                <p className="text-sm" style={{ color: DVA_COLORS.slate }}>
                  Section {currentSection + 1} of {sections.length}
                </p>
              </div>

              {/* Question Cards */}
              <div className="space-y-5">
                {currentSectionQuestions.map((question, idx) => {
                  const questionId = question.question_id || question.id;
                  const hasResponse = responses[questionId] && responses[questionId] !== '';
                  
                  return (
                    <div 
                      key={idx} 
                      className="bg-white rounded-2xl p-6 transition-all duration-200 hover:shadow-lg"
                      style={{ 
                        border: `1px solid ${hasResponse ? DVA_COLORS.orange : DVA_COLORS.border}`,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                      }}
                    >
                      <div className="flex items-start justify-between gap-4 mb-4">
                        <h3 
                          className="text-base font-medium leading-relaxed"
                          style={{ color: DVA_COLORS.navy }}
                        >
                          {question.question_text || question.text || question.question}
                          {question.required && (
                            <span className="ml-1" style={{ color: DVA_COLORS.orange }}>*</span>
                          )}
                        </h3>
                        {hasResponse && (
                          <div 
                            className="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
                            style={{ backgroundColor: DVA_COLORS.orangeLight }}
                          >
                            <Check className="w-4 h-4" style={{ color: DVA_COLORS.orange }} strokeWidth={3} />
                          </div>
                        )}
                      </div>
                      {question.description && (
                        <p className="text-sm mb-4" style={{ color: DVA_COLORS.slate }}>
                          {question.description}
                        </p>
                      )}
                      {renderQuestion(question)}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ============================================================
              BOTTOM NAVIGATION BAR
              ============================================================ */}
          <div 
            className="px-8 py-4 border-t"
            style={{ backgroundColor: 'white', borderColor: DVA_COLORS.border }}
          >
            <div className="max-w-3xl mx-auto flex justify-between items-center">
              {/* Previous Button */}
              <button
                onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
                disabled={currentSection === 0}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm transition-all duration-200"
                style={{
                  backgroundColor: currentSection === 0 ? '#F1F5F9' : 'white',
                  color: currentSection === 0 ? '#94A3B8' : DVA_COLORS.navy,
                  border: currentSection === 0 ? '2px solid #E2E8F0' : `2px solid ${DVA_COLORS.border}`,
                  cursor: currentSection === 0 ? 'not-allowed' : 'pointer',
                }}
                onMouseEnter={(e) => {
                  if (currentSection !== 0) {
                    e.currentTarget.style.backgroundColor = DVA_COLORS.slateLight;
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentSection !== 0) {
                    e.currentTarget.style.backgroundColor = 'white';
                  }
                }}
              >
                <ArrowLeft className="w-4 h-4" />
                Previous
              </button>

              {/* Right Side Actions */}
              <div className="flex items-center gap-3">
                {/* Continue Later - Secondary */}
                <button
                  onClick={handleSaveAndReturn}
                  className="px-5 py-2.5 rounded-xl font-medium text-sm transition-all duration-200"
                  style={{
                    backgroundColor: 'white',
                    color: DVA_COLORS.slate,
                    border: `2px solid ${DVA_COLORS.border}`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = DVA_COLORS.slateLight;
                    e.currentTarget.style.color = DVA_COLORS.navy;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.color = DVA_COLORS.slate;
                  }}
                >
                  Continue later
                </button>
                
                {/* Primary Action */}
                {currentSection === sections.length - 1 ? (
                  <button
                    onClick={handleComplete}
                    disabled={isCompleting}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: DVA_COLORS.orange,
                      color: 'white',
                    }}
                    onMouseEnter={(e) => {
                      if (!isCompleting) e.currentTarget.style.backgroundColor = '#E55A3A';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = DVA_COLORS.orange;
                    }}
                  >
                    <Check className="w-4 h-4" />
                    {isCompleting ? 'Generating summary...' : 'Complete Module'}
                  </button>
                ) : (
                  <button
                    onClick={() => setCurrentSection(Math.min(sections.length - 1, currentSection + 1))}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 shadow-sm hover:shadow-md"
                    style={{
                      backgroundColor: DVA_COLORS.navy,
                      color: 'white',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#0F2642'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = DVA_COLORS.navy}
                  >
                    Next
                    <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UnifiedSurvey;